import React, { useEffect, useState } from "react";
import { Link, useLocation, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Mic, Armchair, Sparkles, Clock, Wind, Car, Camera, Film, Users,
  IndianRupee, MapPin, Phone, Mail, ArrowUpRight, Zap, ShieldCheck, Volume2,
  Menu, X,
} from "lucide-react";
import BookingDialog from "@/components/BookingDialog";
import WhatsAppButton from "@/components/WhatsAppButton";
import Gallery from "@/components/Gallery";
import StudioSetsGallery from "@/components/StudioSetsGallery";
import Showreel from "@/components/Showreel";
import EquipmentStrip from "@/components/EquipmentStrip";
import {
  STUDIO_LOGO, STUDIO_SETS, FEATURES, HERO_IMAGES, STUDIO_ADDRESS,
  WHATSAPP_NUMBER, PHONE_DISPLAY,
} from "@/lib/lime-data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = { Mic, Armchair, Sparkles, Clock, Wind, Car, Camera, Film, Users };

const NAV_LINKS = [
  { href: "#studios", label: "Studios", testid: "nav-studios" },
  { href: "#gallery", label: "Gallery", testid: "nav-gallery" },
  { href: "/pricing", label: "Pricing", testid: "nav-pricing", route: true },
  { href: "#studio-sets", label: "Sets", testid: "nav-sets" },
  { href: "#gears", label: "Gears", testid: "nav-gears" },
  { href: "#showreel", label: "Showreel", testid: "nav-showreel" },
  { href: "#location", label: "Visit", testid: "nav-location" },
  { href: "#contact", label: "Contact", testid: "nav-contact" },
];

export const Nav = ({ onBook }) => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const onLanding = location.pathname === "/";

  // Prefix hash-links with "/" when we're not on landing so they navigate home first
  const resolveHref = (l) => {
    if (l.route) return l.href;
    return onLanding ? l.href : `/${l.href}`;
  };

  // Lock body scroll while menu open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const renderLink = (l, className, extraProps = {}) => {
    if (l.route) {
      return (
        <Link
          key={l.href}
          to={l.href}
          className={className}
          data-testid={extraProps.testid || l.testid}
          onClick={extraProps.onClick}
        >
          {l.label}
        </Link>
      );
    }
    return (
      <a
        key={l.href}
        href={resolveHref(l)}
        className={className}
        data-testid={extraProps.testid || l.testid}
        onClick={extraProps.onClick}
      >
        {l.label}
      </a>
    );
  };

  return (
    <header
      className="fixed top-0 left-0 right-0 z-40 backdrop-blur-xl bg-[#0a0a0a]/70 border-b border-[#262626]"
      data-testid="main-nav"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-20 sm:h-24">
        <Link to="/" className="flex items-center" data-testid="logo-link" onClick={() => setOpen(false)}>
          <img src={STUDIO_LOGO} alt="LIME Studios" className="h-10 sm:h-14 w-auto object-contain" />
        </Link>
        <nav className="hidden md:flex items-center gap-8 text-sm text-[#A3A3A3]">
          {NAV_LINKS.map((l) => renderLink(l, "hover:text-[#f4f4f0] transition-colors"))}
        </nav>
        <div className="flex items-center gap-2">
          <Button
            onClick={onBook}
            data-testid="nav-book-button"
            className="bg-[#E4FF00] text-black hover:bg-[#CDE600] rounded-none font-bold px-4 sm:px-5 text-sm sm:text-base"
          >
            Book Now <ArrowUpRight className="ml-1 h-4 w-4" />
          </Button>
          <button
            type="button"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-hamburger"
            className="md:hidden w-10 h-10 flex items-center justify-center border border-[#262626] text-[#f4f4f0] hover:border-[#E4FF00] hover:text-[#E4FF00] transition-colors"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile menu panel */}
      <div
        className={`md:hidden overflow-hidden border-t border-[#262626] bg-[#0a0a0a]/95 backdrop-blur-xl transition-[max-height] duration-300 ease-out ${open ? "max-h-[80vh]" : "max-h-0"}`}
        data-testid="mobile-menu-panel"
      >
        <nav className="px-4 py-4 flex flex-col">
          {NAV_LINKS.map((l) =>
            renderLink(
              l,
              "py-3 border-b border-[#1a1a1a] text-[#f4f4f0] text-base font-display hover:text-[#E4FF00] transition-colors",
              { testid: `mobile-${l.testid}`, onClick: () => setOpen(false) },
            ),
          )}
        </nav>
      </div>
    </header>
  );
};

const HERO_SLIDE_INTERVAL_MS = 5000;
const HERO_FADE_MS = 1200;

const Hero = ({ onBook }) => {
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    if (HERO_IMAGES.length <= 1) return;
    const t = setInterval(() => {
      setActiveIdx((i) => (i + 1) % HERO_IMAGES.length);
    }, HERO_SLIDE_INTERVAL_MS);
    return () => clearInterval(t);
  }, []);

  return (
    <section id="top" className="relative min-h-screen flex items-end overflow-hidden grain">
      {HERO_IMAGES.map((src, i) => (
        <div
          key={src}
          aria-hidden={i !== activeIdx}
          className="absolute inset-0"
          style={{
            backgroundImage: `linear-gradient(180deg, rgba(10,10,10,0.55) 0%, rgba(10,10,10,0.92) 85%), url(${src})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            opacity: i === activeIdx ? 1 : 0,
            transition: `opacity ${HERO_FADE_MS}ms ease-in-out`,
          }}
        />
      ))}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 pt-32 w-full">
      <div className="font-mono-overline text-[#E4FF00] mb-6">Baner · Pune · 24×7</div>
      <h1 className="font-display text-5xl sm:text-7xl lg:text-[8rem] font-black leading-[0.85] tracking-tighter">
        <span className="lime-gradient-text">Pro-grade studio.</span>
        <br />
        <span className="text-[#f4f4f0]">Zero hassle</span>
        <span className="text-[#E4FF00]">.</span>
      </h1>
      <p className="mt-8 max-w-2xl text-base sm:text-lg text-[#A3A3A3] leading-relaxed">
        A sound-treated creative space for content creators, filmmakers, brands and agencies.
        Walk in with an idea. Walk out with the shoot.
      </p>
      <div className="mt-10 flex flex-wrap items-center gap-4">
        <Button
          onClick={onBook}
          data-testid="hero-book-button"
          className="bg-[#E4FF00] text-black hover:bg-[#CDE600] rounded-none font-bold px-8 py-6 text-base"
        >
          Book Your Slot <ArrowUpRight className="ml-2 h-5 w-5" />
        </Button>
        <a
          href="#studio-sets"
          data-testid="hero-explore-link"
          className="px-8 py-4 border border-[#262626] text-[#f4f4f0] hover:border-[#E4FF00] hover:text-[#E4FF00] transition-colors font-medium"
        >
          Explore sets
        </a>
      </div>

      <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-px bg-[#262626] border border-[#262626]">
        {[
          { k: "Studios", v: "Multiple services" },
          { k: "Open", v: "24 × 7" },
          { k: "Acoustic", v: "Treated" },
          { k: "Starts at", v: "₹1,300/hr" },
        ].map((s) => (
          <div key={s.k} className="bg-[#0a0a0a] px-5 py-5">
            <div className="font-mono-overline text-[#E4FF00]">{s.k}</div>
            <div className="font-display text-xl sm:text-2xl font-bold mt-1">{s.v}</div>
          </div>
        ))}
      </div>
    </div>
  </section>
  );
};

const Marquee = () => (
  <div className="border-y border-[#262626] overflow-hidden bg-[#0a0a0a] py-6">
    <div className="ticker flex gap-12 whitespace-nowrap font-display text-3xl sm:text-5xl font-black tracking-tighter text-[#262626]">
      {["a", "b"].map((k) => (
        <React.Fragment key={`marquee-${k}`}>
          <span>PODCASTS</span><span className="text-[#E4FF00]">●</span>
          <span>PHOTOSHOOTS</span><span className="text-[#E4FF00]">●</span>
          <span>VIDEO PRODUCTION</span><span className="text-[#E4FF00]">●</span>
          <span>BRAND CONTENT</span><span className="text-[#E4FF00]">●</span>
          <span>INTERVIEWS</span><span className="text-[#E4FF00]">●</span>
          <span>SOUND TREATED</span><span className="text-[#E4FF00]">●</span>
        </React.Fragment>
      ))}
    </div>
  </div>
);

const studioCardSpanClass = (idx) =>
  idx === 0 ? "md:col-span-7 md:row-span-2 min-h-[420px]" : "md:col-span-5 min-h-[260px]";

const StudiosShowcase = ({ onBook }) => (
  <section id="studios" className="py-24 sm:py-32">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
        <div>
          <div className="font-mono-overline text-[#E4FF00]">Multiple services · One roof</div>
          <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-3">
            Built for every shoot.
          </h2>
        </div>
        <p className="max-w-md text-sm text-[#A3A3A3]">
          Whether you're recording a long-form podcast, shooting brand stills, or producing a multi-cam video — there's a dedicated space ready for you.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
        {STUDIO_SETS.map((s, idx) => (
          <div
            key={s.id}
            data-testid={`studio-card-${s.id}`}
            className={`group relative overflow-hidden border border-[#262626] hover:border-[#E4FF00]/60 transition-colors duration-300 ${studioCardSpanClass(idx)}`}
          >
            <div
              className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
              style={{ backgroundImage: `url(${s.image})` }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
            <div className="relative h-full flex flex-col justify-end p-6">
              <div className="font-mono-overline text-[#E4FF00] mb-2">0{idx + 1} · {s.id}</div>
              <h3 className="font-display text-3xl sm:text-4xl font-black tracking-tighter text-[#f4f4f0]">{s.title}</h3>
              <p className="text-sm text-[#A3A3A3] mt-2 max-w-md">{s.blurb}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {s.tags.map((t) => (
                  <Badge key={t} className="bg-[#141414]/80 border border-[#262626] text-[#f4f4f0] font-mono text-[10px] rounded-none">
                    {t}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-10">
        <Button
          onClick={onBook}
          data-testid="studios-book-button"
          className="bg-transparent border border-[#E4FF00] text-[#E4FF00] hover:bg-[#E4FF00] hover:text-black rounded-none font-bold px-7 py-5"
        >
          Reserve a set <ArrowUpRight className="ml-1 h-4 w-4" />
        </Button>
      </div>
    </div>
  </section>
);

const Features = () => (
  <section id="features" className="py-24 sm:py-32 border-t border-[#262626] bg-[#0a0a0a]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-4">
          <div className="font-mono-overline text-[#E4FF00]">Why LIME</div>
          <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-3">
            Everything you need.<br/>Nothing you don't.
          </h2>
          <p className="text-sm text-[#A3A3A3] mt-5 max-w-sm">
            We obsess over the production basics so you can obsess over your craft.
            From acoustic treatment to a makeup console — it's all in.
          </p>
        </div>

        <div className="md:col-span-8 grid grid-cols-2 sm:grid-cols-3 gap-px bg-[#262626] border border-[#262626]">
          {FEATURES.map((f) => {
            const Icon = ICONS[f.icon] || Zap;
            return (
              <div key={f.label} className="bg-[#0a0a0a] p-6 hover:bg-[#141414] transition-colors duration-300">
                <Icon className="h-7 w-7 text-[#E4FF00]" strokeWidth={1.5} />
                <div className="font-display font-bold text-base text-[#f4f4f0] mt-5 leading-tight">{f.label}</div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { icon: Volume2, title: "Sound-treated rooms", body: "Acoustic panels, bass traps and isolation — broadcast-grade audio out of the box." },
          { icon: ShieldCheck, title: "Pro team on standby", body: "Videographers, audio engineers and editors available across all corporate packages." },
          { icon: Zap, title: "Hassle-free setup", body: "Walk in, plug in, hit record. Backdrops, mics and lights are pre-rigged." },
        ].map((c) => (
          <div key={c.title} className="border border-[#262626] p-8 bg-[#0a0a0a] hover:border-[#E4FF00]/40 transition-colors">
            <c.icon className="h-8 w-8 text-[#E4FF00]" strokeWidth={1.5} />
            <h3 className="font-display text-2xl font-bold tracking-tight mt-5">{c.title}</h3>
            <p className="text-sm text-[#A3A3A3] mt-2 leading-relaxed">{c.body}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Location = () => (
  <section id="location" className="py-24 sm:py-32 border-t border-[#262626] bg-[#0a0a0a]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-12 gap-8 items-stretch">
      <div className="md:col-span-5 flex flex-col">
        <div className="font-mono-overline text-[#E4FF00]">Visit</div>
        <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-3">
          Find us in Baner.
        </h2>
        <p className="text-[#A3A3A3] mt-5 leading-relaxed text-sm">
          {STUDIO_ADDRESS}
        </p>
        <div className="mt-8 space-y-4">
          <div className="flex items-start gap-3">
            <MapPin className="h-5 w-5 text-[#E4FF00] mt-0.5" />
            <span className="text-sm text-[#f4f4f0]">605, Montvert Velocity, Baner–Pashan Link Road</span>
          </div>
          <div className="flex items-center gap-3">
            <Phone className="h-5 w-5 text-[#E4FF00]" />
            <a href={`tel:+${WHATSAPP_NUMBER}`} className="text-sm hover:text-[#E4FF00] transition-colors" data-testid="location-phone">{PHONE_DISPLAY}</a>
          </div>
          <div className="flex items-center gap-3">
            <Mail className="h-5 w-5 text-[#E4FF00]" />
            <a href="mailto:bookings@limestudiospune.com" className="text-sm hover:text-[#E4FF00] transition-colors" data-testid="location-email">bookings@limestudiospune.com</a>
          </div>
        </div>
        <a
          href={`https://wa.me/${WHATSAPP_NUMBER}`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="location-whatsapp"
          className="mt-8 inline-flex items-center gap-2 px-6 py-3 bg-[#25D366] text-black font-bold w-fit"
        >
          Quick chat on WhatsApp <ArrowUpRight className="h-4 w-4" />
        </a>
      </div>

      <div className="md:col-span-7 border border-[#262626] overflow-hidden min-h-[400px]">
        <iframe
          title="LIME Studios on Map"
          src="https://www.google.com/maps?q=Montvert+Velocity+Baner+Pashan+Link+Road+Pune&output=embed"
          width="100%"
          height="100%"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          style={{ filter: "grayscale(1) invert(0.92) contrast(0.9)", border: 0, minHeight: 400 }}
        />
      </div>
    </div>
  </section>
);

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thanks! We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Couldn't send. Try WhatsApp instead.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 sm:py-32 border-t border-[#262626]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="font-mono-overline text-[#E4FF00] text-center">Got a brief?</div>
        <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tighter text-center mt-3">
          Tell us about your shoot.
        </h2>
        <form onSubmit={submit} className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="contact-form">
          <div>
            <Label className="font-mono-overline text-[#A3A3A3]">Name</Label>
            <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 bg-[#0a0a0a] border-[#262626] rounded-none focus:border-[#E4FF00]" data-testid="contact-name-input" />
          </div>
          <div>
            <Label className="font-mono-overline text-[#A3A3A3]">Phone</Label>
            <Input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-2 bg-[#0a0a0a] border-[#262626] rounded-none focus:border-[#E4FF00]" data-testid="contact-phone-input" />
          </div>
          <div className="sm:col-span-2">
            <Label className="font-mono-overline text-[#A3A3A3]">Email</Label>
            <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2 bg-[#0a0a0a] border-[#262626] rounded-none focus:border-[#E4FF00]" data-testid="contact-email-input" />
          </div>
          <div className="sm:col-span-2">
            <Label className="font-mono-overline text-[#A3A3A3]">Project</Label>
            <Textarea required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={4} className="mt-2 bg-[#0a0a0a] border-[#262626] rounded-none focus:border-[#E4FF00]" data-testid="contact-message-input" />
          </div>
          <div className="sm:col-span-2 flex justify-center pt-3">
            <Button disabled={submitting} type="submit" data-testid="contact-submit-button" className="bg-[#E4FF00] text-black hover:bg-[#CDE600] rounded-none font-bold px-10 py-6">
              {submitting ? "Sending…" : "Send brief"}
            </Button>
          </div>
        </form>
      </div>
    </section>
  );
};

export const Footer = () => (
  <footer className="border-t border-[#262626] bg-[#0a0a0a] pt-16 pb-10">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="font-display text-[18vw] sm:text-[14vw] leading-none font-black tracking-tighter text-[#141414] select-none">
        LIME
      </div>

      {/* Instagram CTA — big, prominent */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-12 gap-6 items-center border border-[#262626] hover:border-[#E4FF00]/60 transition-colors p-6 sm:p-10" data-testid="footer-instagram-cta">
        <a
          href="https://www.instagram.com/limestudiospune/"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="footer-instagram-link"
          className="md:col-span-7 group flex items-center gap-5"
        >
          <span className="relative shrink-0 flex items-center justify-center h-16 w-16 sm:h-20 sm:w-20 rounded-2xl bg-gradient-to-tr from-[#feda75] via-[#d62976] to-[#4f5bd5] text-white shadow-lg group-hover:scale-105 transition-transform">
            <svg viewBox="0 0 24 24" className="h-8 w-8 sm:h-10 sm:w-10 fill-white" aria-hidden="true">
              <path d="M12 2.2c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.05.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23a3.71 3.71 0 0 1-.9 1.38c-.42.42-.82.68-1.38.9-.42.16-1.05.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41a3.71 3.71 0 0 1-1.38-.9 3.71 3.71 0 0 1-.9-1.38c-.16-.42-.36-1.05-.41-2.23C2.21 15.58 2.2 15.2 2.2 12s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.05-.36 2.23-.41C8.42 2.21 8.8 2.2 12 2.2zm0 1.8c-3.15 0-3.5.01-4.74.07-1.04.05-1.6.22-1.98.36-.5.19-.85.43-1.22.8-.37.37-.61.72-.8 1.22-.14.38-.31.94-.36 1.98-.06 1.24-.07 1.59-.07 4.74s.01 3.5.07 4.74c.05 1.04.22 1.6.36 1.98.19.5.43.85.8 1.22.37.37.72.61 1.22.8.38.14.94.31 1.98.36 1.24.06 1.59.07 4.74.07s3.5-.01 4.74-.07c1.04-.05 1.6-.22 1.98-.36.5-.19.85-.43 1.22-.8.37-.37.61-.72.8-1.22.14-.38.31-.94.36-1.98.06-1.24.07-1.59.07-4.74s-.01-3.5-.07-4.74c-.05-1.04-.22-1.6-.36-1.98a3.3 3.3 0 0 0-.8-1.22 3.3 3.3 0 0 0-1.22-.8c-.38-.14-.94-.31-1.98-.36-1.24-.06-1.59-.07-4.74-.07zm0 3.06a4.94 4.94 0 1 1 0 9.88 4.94 4.94 0 0 1 0-9.88zm0 1.8a3.14 3.14 0 1 0 0 6.28 3.14 3.14 0 0 0 0-6.28zm5.16-2a1.18 1.18 0 1 1 0 2.36 1.18 1.18 0 0 1 0-2.36z"/>
            </svg>
          </span>
          <div>
            <div className="font-mono-overline text-[#E4FF00]">Instagram · @limestudiospune</div>
            <h3 className="font-display text-2xl sm:text-4xl font-black tracking-tighter mt-2 leading-[0.95] text-[#f4f4f0] group-hover:text-[#E4FF00] transition-colors">
              Peek behind the lens.
            </h3>
            <p className="text-sm text-[#A3A3A3] mt-2 max-w-md">
              Daily drops from the LIME floor — sets in build, shoot-day chaos, reels you wish you'd shot. Follow along.
            </p>
          </div>
        </a>

        <div className="md:col-span-5 flex md:justify-end">
          <a
            href="https://www.instagram.com/limestudiospune/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-instagram-cta-button"
            className="inline-flex items-center gap-2 bg-[#E4FF00] text-black px-7 py-4 font-display font-black tracking-tight text-base sm:text-lg hover:bg-[#CDE600] transition-colors"
          >
            Follow on Instagram
            <span aria-hidden="true">↗</span>
          </a>
        </div>
      </div>

      <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-8 text-sm">
        <div>
          <div className="font-mono-overline text-[#E4FF00]">Studio</div>
          <ul className="mt-3 space-y-2 text-[#A3A3A3]">
            <li><a href="/#studios" className="hover:text-[#f4f4f0]">Photoshoot</a></li>
            <li><a href="/#studios" className="hover:text-[#f4f4f0]">Video</a></li>
            <li><a href="/#studios" className="hover:text-[#f4f4f0]">Podcast</a></li>
            <li><Link to="/pricing" className="hover:text-[#f4f4f0]" data-testid="footer-pricing-link">Pricing</Link></li>
          </ul>
        </div>
        <div>
          <div className="font-mono-overline text-[#E4FF00]">Company</div>
          <ul className="mt-3 space-y-2 text-[#A3A3A3]">
            <li><a href="https://www.lifeinsidemedia.com" target="_blank" rel="noopener noreferrer" className="hover:text-[#f4f4f0] transition-colors" data-testid="footer-lim-link">By Life Inside Media ↗</a></li>
            <li><a href="#features" className="hover:text-[#f4f4f0]">Why us</a></li>
            <li><a href="#contact" className="hover:text-[#f4f4f0]">Contact</a></li>
          </ul>
        </div>
        <div>
          <div className="font-mono-overline text-[#E4FF00]">Reach</div>
          <ul className="mt-3 space-y-2 text-[#A3A3A3]">
            <li>{PHONE_DISPLAY}</li>
            <li><a href="https://maps.google.com/?q=LIME+Studios+Montvert+Velocity+Baner+Pashan+Link+Road+Pune" target="_blank" rel="noopener noreferrer" className="hover:text-[#f4f4f0] transition-colors" data-testid="footer-map-link">Baner, Pune ↗</a></li>
          </ul>
        </div>
        <div>
          <div className="font-mono-overline text-[#E4FF00]">Hours</div>
          <ul className="mt-3 space-y-2 text-[#A3A3A3]">
            <li>24 × 7 — by appointment</li>
            <li>Same-day bookings available</li>
          </ul>
        </div>
      </div>
      <div className="mt-12 pt-6 border-t border-[#262626] flex flex-wrap justify-between gap-3 text-xs font-mono text-[#666] uppercase tracking-widest">
        <span>© {new Date().getFullYear()} LIME Studios · Life Inside Media</span>
        <span>Made in Pune · India</span>
      </div>
    </div>
  </footer>
);

export default function LimeLanding() {
  const [packages, setPackages] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [initialPkg, setInitialPkg] = useState(null);
  const { packageId } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/packages`);
        setPackages(data);
      } catch (e) {
        toast.error("Couldn't load packages");
      }
    };
    load();
  }, []);

  // Deep-link: /book/:packageId auto-opens the dialog with that package pre-selected
  useEffect(() => {
    if (!packageId || packages.length === 0) return;
    const match = packages.find((p) => p.id === packageId);
    if (match) {
      setInitialPkg(match);
      setOpenDialog(true);
    } else {
      toast.error("Unknown booking link — please pick a package below.");
      navigate("/", { replace: true });
    }
  }, [packageId, packages, navigate]);

  const openBooking = (pkg) => {
    setInitialPkg(pkg || null);
    setOpenDialog(true);
  };

  // When user closes the dialog from a /book/:id deep-link, return to home URL
  const handleOpenChange = (next) => {
    setOpenDialog(next);
    if (!next && packageId) {
      navigate("/", { replace: true });
    }
  };

  return (
    <div className="bg-[#0a0a0a] text-[#f4f4f0] min-h-screen">
      <Nav onBook={() => openBooking()} />
      <Hero onBook={() => openBooking()} />
      <Marquee />
      <StudiosShowcase onBook={() => openBooking()} />
      <Gallery />
      <StudioSetsGallery />
      <Features />
      <EquipmentStrip />
      <Showreel />
      <Location />
      <Contact />
      <Footer />
      <WhatsAppButton />
      <BookingDialog
        open={openDialog}
        onOpenChange={setOpenDialog}
        initialPackage={initialPkg}
        packages={packages}
      />
    </div>
  );
}
